import type {
  ApiResponse,
  PaginatedResponse,
} from "@employee-management/common";
import { authService } from "./auth-service";
import type { AppSettings } from "@/types/app-settings.types";

// Get the authenticated axios instance from auth service
const api = authService.api;

// Generic API methods
export const apiService = {
  // GET request
  get: async <T>(url: string): Promise<T> => {
    const response = await api.get<ApiResponse<T>>(url);
    if (!response.data.success || response.data.data === undefined) {
      throw new Error(
        response.data.error?.message || "No data received from server"
      );
    }
    return response.data.data;
  },

  getBlob: async (url: string): Promise<Blob> => {
    const response = await api.get<Blob>(url, {
      responseType: "blob",
    });
    return response.data;
  },

  getPaginated: async <T>(url: string): Promise<PaginatedResponse<T>> => {
    const response = await api.get<PaginatedResponse<T>>(url);
    if (!response.data.success || response.data.data === undefined) {
      throw new Error(
        response.data.error?.message || "No data received from server"
      );
    }
    return response.data;
  },

  // POST request
  post: async <T>(url: string, data?: unknown): Promise<T> => {
    const response = await api.post<ApiResponse<T>>(url, data);
    if (!response.data.success || response.data.data === undefined) {
      throw new Error(
        response.data.error?.message || "No data received from server"
      );
    }
    return response.data.data;
  },

  // PUT request
  put: async <T>(url: string, data?: unknown): Promise<T> => {
    const response = await api.put<ApiResponse<T>>(url, data);
    if (!response.data.success || response.data.data === undefined) {
      throw new Error(
        response.data.error?.message || "No data received from server"
      );
    }
    return response.data.data;
  },

  // DELETE request
  delete: async <T>(url: string): Promise<T> => {
    const response = await api.delete<ApiResponse<T>>(url);

    console.log(response.data);

    if (!response.data.success) {
      throw new Error(
        response.data.error?.message || "No data received from server"
      );
    }
    return response.data.data as T;
  },

  // PATCH request
  patch: async <T>(url: string, data?: unknown): Promise<T> => {
    const response = await api.patch<ApiResponse<T>>(url, data);
    if (!response.data.success || response.data.data === undefined) {
      throw new Error(
        response.data.error?.message || "No data received from server"
      );
    }
    return response.data.data;
  },
};

// Export the raw axios instance for cases where you need more control
export { api as apiClient };

// App Settings API methods
export const appSettingsService = {
  // Get all app settings
  getAll: async (): Promise<AppSettings> => {
    return apiService.get<AppSettings>("/app-settings");
  },

  // Get specific app setting by key
  getByKey: async (key: string): Promise<{ key: string; value: any }> => {
    return apiService.get<{ key: string; value: any }>(`/app-settings/${key}`);
  },

  // Save app setting
  save: async (
    key: string,
    value: any
  ): Promise<{ key: string; value: any }> => {
    return apiService.post<{ key: string; value: any }>("/app-settings", {
      key,
      value,
    });
  },

  // Delete app setting
  delete: async (key: string): Promise<void> => {
    return apiService.delete<void>(`/app-settings/${key}`);
  },
};

// Reports API methods
export const reportsService = {
  // Get unique list of client names
  getClientList: async (): Promise<string[]> => {
    return apiService.get<string[]>("/reports/client-list");
  },

  // Get project reports with metadata
  getProjectReports: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    status?: string;
    client?: string;
  }): Promise<PaginatedResponse<any>> => {
    const searchParams = new URLSearchParams();

    if (params?.page) searchParams.append("page", params.page.toString());
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    if (params?.search) searchParams.append("search", params.search);
    if (params?.sortBy) searchParams.append("sortBy", params.sortBy);
    if (params?.sortOrder) searchParams.append("sortOrder", params.sortOrder);
    if (params?.status) searchParams.append("status", params.status);
    if (params?.client) searchParams.append("client", params.client);

    const url = `/reports/projects${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
    return apiService.getPaginated<any>(url);
  },
};
